import React, { useState } from "react";
import { Link } from "react-router-dom";
import { pauseIntroductions, resumeIntroductions } from "../services/userService";
import {
  PAUSE_REASONS,
  NOTE_MAX,
  isPaused,
  pauseLine,
  onLine,
  formatEt,
  validatePauseNote,
  firstNameOf,
  closedSummary,
  closedItemText,
  wasAlready,
  resumeOutlook,
  countedAsEndText,
  waitUntilText,
  repeatPauseClosedSummary,
  RESUME_AS_END_HOURS,
} from "./introPauseUtils";

/** What a pause closed, each linked to its pair page. */
const ClosedList = ({ closed, pauserFirst }) =>
  Array.isArray(closed) && closed.length > 0 ? (
    <ul className="mb-0 ps-20" style={{ listStyle: "disc" }}>
      {closed.map((c, i) => (
        <li key={c.pairId || i}>
          {c.pairId ? (
            <Link to={`/compatibility-control/view/${c.pairId}`}>{closedItemText(c, pauserFirst)}</Link>
          ) : (
            closedItemText(c, pauserFirst)
          )}
        </li>
      ))}
    </ul>
  ) : null;

/**
 * Introductions on the member page: whether they are paused, and the support
 * buttons (founder go 2026-09-27, "yes on 1-4").
 *
 * WHY A FORM ON THE PAGE AND NOT A POPUP. The note is the audit trail (it
 * lands in the admin notes and the activity log), so it gets a real textarea
 * with its count in view, and the facts of what a pause does sit right above
 * the button that does it. Every line here is what the server does (backend
 * api/utils/introPause.js): if the server changes, change the words with it.
 *
 * The result lives in the PARENT (`result`), because the refresh after a
 * change swaps the whole page for the loader and would drop it from here.
 */
const IntroPausePanel = ({ user, canEdit, result, onChanged, onDismissResult, onUnauthorized }) => {
  const paused = isPaused(user);
  const firstName = firstNameOf(user && user.fullName);
  const who = firstName || "this member";
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => {
    setOpen(false);
    setNote("");
    setReason("");
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const invalid = validatePauseNote(note);
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = paused
        ? await resumeIntroductions(user._id, { note: note.trim() })
        : await pauseIntroductions(user._id, { note: note.trim(), reason: reason || undefined });
      const data = (res && res.data) || {};
      if (data.success === false) {
        setError(data.message || "The server refused this. Nothing was changed.");
        return;
      }
      close();
      if (onChanged) onChanged({ kind: paused ? "resumed" : "paused", firstName, data });
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 401 && onUnauthorized) {
        onUnauthorized();
        return;
      }
      const serverMessage = err && err.response && err.response.data && err.response.data.message;
      // A refusal (4xx) changed nothing. Anything else (a timeout, a 5xx) may
      // have landed, so never claim it did not.
      setError(
        serverMessage ||
          (status && status < 500
            ? `The server refused this (HTTP ${status}). Nothing was changed.`
            : "The server did not confirm this. Reload the page to see the current state before trying again.")
      );
    } finally {
      setBusy(false);
    }
  };

  const resultWho = (result && result.firstName) || "this member";
  const resultAlready = !!(result && wasAlready(result.kind, result.data));
  const outlook = paused ? resumeOutlook(user.introPause) : null;

  return (
    <div className="col-md-12 mb-3">
      <div className="border radius-8 px-16 py-12">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <label className="form-label fw-bold me-2 mb-0">Introductions:</label>
            {paused ? (
              <span className="bg-warning-100 text-warning-700 px-8 py-2 radius-4 fw-medium" data-testid="intro-pause-line">
                {pauseLine(user.introPause)}
              </span>
            ) : (
              <span data-testid="intro-pause-line">{onLine(user && user.introPause)}</span>
            )}
          </div>
          {canEdit && !open && (
            <button
              type="button"
              className={paused ? "btn btn-primary btn-sm" : "btn btn-outline-warning btn-sm"}
              onClick={() => {
                setOpen(true);
                setError("");
              }}
            >
              {paused ? "Resume introductions" : "Pause introductions"}
            </button>
          )}
        </div>

        {open && (
          <form onSubmit={submit} className="mt-12" noValidate>
            {/* Each line is what the server does (backend utils/introPause,
                dailyCadence, truthGate PAUSE_SILENCED, nudgeGate,
                curiouzzPromise). Review 2026-09-27: no line may claim more. */}
            {paused ? (
              <ul className="text-sm text-secondary-light mb-12 ps-20" style={{ listStyle: "disc", lineHeight: 1.6 }}>
                <li>
                  Resuming is instant: the pause ends now, and the next deal, or their own stage, can introduce {who}{" "}
                  once someone fits and no wait is left.
                </li>
                <li>A wait {who} already had before the pause still stands (from an End of their own, or a lapse timeout).</li>
                {/* 2026-09-27: a resume from this page never counts the
                    pause's close as an End. Only the member's own tap in the
                    2.4 app within 24 hours does, and the app names the wait
                    before that tap. Support has no screen to warn them with. */}
                <li data-testid="intro-resume-as-end">
                  A resume from this page never adds a wait of the pause's making. (Only {who}'s own tap in the 2.4 app,
                  within {RESUME_AS_END_HOURS} hours of a pause that closed an introduction, counts that close as an End,
                  and the app tells them the wait before they tap.)
                </li>
                {/* introPause.resumeWaitUntil: the server's own answer, with
                    the wait rules of a real End. Worded as "not before", which
                    stays true whatever else holds them back. */}
                {outlook.waitUntil ? (
                  <li className="fw-semibold" data-testid="intro-resume-wait">
                    If {who} resumes now, there is no new introduction for them before {outlook.waitUntil}.
                  </li>
                ) : outlook.noWait && outlook.countsAsEnd === true ? (
                  <li data-testid="intro-resume-wait">If {who} resumes now, that End adds no wait.</li>
                ) : null}
                <li>Nothing is sent to {who}. Reply to them yourself.</li>
              </ul>
            ) : (
              <ul className="text-sm text-secondary-light mb-12 ps-20" style={{ listStyle: "disc", lineHeight: 1.6 }}>
                <li className="fw-semibold">Only on the member's own written request.</li>
                <li>Nobody new is introduced to {who}, and {who} is not introduced to anyone.</li>
                <li>
                  Any open introduction that has not been revealed closes now. The other person gets no strike now, and
                  after the usual short wait (about 30 minutes) gets a next introduction only if someone fits. They are
                  told{" "}
                  {firstName ? `"${firstName} is taking a break"` : "this member is taking a break"} only if they had
                  answered a question or had already been told about the introduction.
                </li>
                <li>
                  Every introduction and nudge push to {who} stops: new introductions, the Vault, Reveal, Say hello,
                  Still in? and reply nudges, and the 8 PM Daily Question (the question stays in the app). Chat
                  messages, calls, closings, meetups and account notices still reach them.
                </li>
                {/* Not "their conversations stay" (review 2026-09-27): the
                    other person can still Close one, and the Promise still
                    closes a quiet revealed conversation with no messages
                    (answerPromiseDryRun conversationHeldByPause). */}
                <li>
                  Their conversations are not closed by the pause: chat, calls, meetups and Close keep working. While{" "}
                  {who} is paused, a quiet revealed conversation that has messages in it is not closed automatically; one
                  with no messages can still close on the usual schedule.
                </li>
                <li>
                  On 2.3, their stage says their introductions are paused, as they asked, and to write to us from Help
                  and support to start again.
                </li>
                <li>
                  Resuming later is instant. A resume from this page adds no wait. If {who} turns introductions back on
                  in the 2.4 app within {RESUME_AS_END_HOURS} hours of a pause that closed an introduction, the app
                  first tells them their next introduction waits as after an End, and the other person may get a lapse.
                </li>
                <li>Nothing is sent to {who}. Reply to them yourself.</li>
              </ul>
            )}

            {!paused && (
              <div className="mb-12">
                <label className="form-label mb-4" htmlFor="intro-pause-reason">
                  Reason (optional)
                </label>
                <select
                  id="intro-pause-reason"
                  className="form-select form-select-sm w-auto"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={busy}
                >
                  <option value="">No reason given</option>
                  {PAUSE_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-12">
              <label className="form-label mb-4" htmlFor="intro-pause-note">
                Note (required)
              </label>
              <textarea
                id="intro-pause-note"
                className="form-control"
                rows={2}
                maxLength={NOTE_MAX}
                placeholder={
                  paused
                    ? "What they asked, and where. For example: asked by email on Oct 3 to start again."
                    : "What they asked, and where. For example: asked by email on Sep 27 to stop being introduced."
                }
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  if (error) setError("");
                }}
                disabled={busy}
              />
              <div className="d-flex justify-content-between mt-1">
                <span className="text-danger text-sm" role={error ? "alert" : undefined}>
                  {error}
                </span>
                <span className="text-xs text-secondary-light">
                  {note.trim().length}/{NOTE_MAX}
                </span>
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={close} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className={paused ? "btn btn-primary btn-sm" : "btn btn-warning btn-sm"} disabled={busy}>
                {busy ? (paused ? "Resuming..." : "Pausing...") : paused ? "Resume introductions" : "Pause introductions"}
              </button>
            </div>
          </form>
        )}

        {result && (
          <div className="alert alert-success mt-12 mb-0 py-12 px-16 text-sm" role="status">
            <div className="d-flex justify-content-between gap-2">
              <div>
                {result.kind === "paused" && resultAlready ? (
                  // A second pause (another tab, the member's own tap in
                  // between) writes no new pause and does not save the note,
                  // but still closes any card they were left holding and
                  // returns it in `closed` (backend introPause.pauseMember,
                  // review 2026-09-27 item 1).
                  <>
                    <div className="fw-semibold">
                      {resultWho} was already paused{result.data.at ? ` (since ${formatEt(result.data.at)})` : ""}. Your
                      note was not saved.
                    </div>
                    <div className="mt-1" data-testid="intro-repeat-pause-closed">
                      {repeatPauseClosedSummary(result.data.closed)}
                    </div>
                    <ClosedList closed={result.data.closed} pauserFirst={result.firstName} />
                  </>
                ) : result.kind === "resumed" && resultAlready ? (
                  <div className="fw-semibold">
                    Introductions were already on for {resultWho}. Nothing changed, and your note was not saved.
                  </div>
                ) : result.kind === "paused" ? (
                  <>
                    <div className="fw-semibold">Introductions paused for {resultWho}.</div>
                    <div className="mt-1">{closedSummary(result.data && result.data.closed)}</div>
                    <ClosedList closed={result.data && result.data.closed} pauserFirst={result.firstName} />
                  </>
                ) : (
                  <>
                    <div className="fw-semibold">Introductions are on again for {resultWho}.</div>
                    {/* The 24-hour rule applied (countedAsEnd) and when the
                        wait ends (waitUntil), so support can tell the member
                        (review 2026-09-27, item 2). */}
                    {countedAsEndText(result.data && result.data.countedAsEnd, resultWho) ? (
                      <div className="mt-1" data-testid="intro-resume-counted">
                        {countedAsEndText(result.data.countedAsEnd, resultWho)}
                      </div>
                    ) : null}
                    {waitUntilText(result.data && result.data.waitUntil, resultWho) ? (
                      <div className="mt-1 fw-semibold" data-testid="intro-resume-wait-until">
                        {waitUntilText(result.data.waitUntil, resultWho)}
                      </div>
                    ) : null}
                    {result.data && result.data.nextLine ? (
                      <div className="mt-1">Their next-introduction line: "{result.data.nextLine}"</div>
                    ) : null}
                  </>
                )}
              </div>
              {onDismissResult && (
                <button
                  type="button"
                  className="btn btn-sm p-0 border-0 bg-transparent text-success-600"
                  aria-label="Dismiss"
                  onClick={onDismissResult}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntroPausePanel;
